/**
 * Star Impact text for SP700, SP500, and HSP7000.
 * These models print characters. They do not use Star Graphic or StarPRNT rasters.
 * ESC d 2 feeds to the cutter, then cuts, on models that have a cutter.
 */

import { encodeWindows1252 } from './starLine'

const ESC = 0x1b
const COLUMNS = 42

function clampSize(value: number): number {
    if (!Number.isFinite(value)) return 1
    return Math.min(6, Math.max(1, Math.round(value)))
}

function columnsFor(width: number): number {
    return Math.max(16, Math.floor(COLUMNS / clampSize(width)))
}

function wrapWords(text: string, maxWidth: number): string[] {
    const clean = String(text || '').replace(/\s+/g, ' ').trim()
    if (!clean) return ['']
    if (maxWidth < 1) return [clean]
    const lines: string[] = []
    let current = ''
    for (const word of clean.split(' ')) {
        if (word.length > maxWidth) {
            if (current) {
                lines.push(current)
                current = ''
            }
            for (let i = 0; i < word.length; i += maxWidth) lines.push(word.slice(i, i + maxWidth))
            continue
        }
        const next = current ? `${current} ${word}` : word
        if (next.length <= maxWidth) current = next
        else {
            if (current) lines.push(current)
            current = word
        }
    }
    if (current) lines.push(current)
    return lines.length ? lines : ['']
}

export class StarImpactText {
    private bytes: number[] = [ESC, 0x40]

    constructor(emptyLines = 0) {
        for (let i = 0; i < Math.max(0, emptyLines); i++) this.bytes.push(0x0a, 0x0d)
    }

    line(text: string, _align: number, width: number, _height: number) {
        const limit = columnsFor(width)
        const safe = text.length > limit ? text.slice(0, limit) : text
        this.bytes.push(...encodeWindows1252(safe), 0x0a, 0x0d)
    }

    boldLine(text: string, align: number, width: number, height: number) {
        this.bytes.push(ESC, 0x45)
        this.line(text, align, width, height)
        this.bytes.push(ESC, 0x46)
    }

    lineParts(parts: Array<{ text: string; bold?: boolean }>, _align: number, width: number, _height: number) {
        const limit = columnsFor(width)
        let used = 0
        parts.forEach((part) => {
            if (!part.text || used >= limit) return
            const safe = part.text.slice(0, limit - used)
            used += safe.length
            if (part.bold) this.bytes.push(ESC, 0x45)
            this.bytes.push(...encodeWindows1252(safe))
            if (part.bold) this.bytes.push(ESC, 0x46)
        })
        this.bytes.push(0x0a, 0x0d)
    }

    centered(text: string, width: number, height: number) {
        const columns = columnsFor(width)
        for (const part of wrapWords(text, columns)) {
            const pad = Math.max(0, Math.floor((columns - part.length) / 2))
            this.line(`${' '.repeat(pad)}${part}`, 1, width, height)
        }
    }

    pair(left: string, right: string, width: number, height: number) {
        const columns = columnsFor(width)
        const line = left.length + 1 + right.length <= columns
            ? left + ' '.repeat(columns - left.length - right.length) + right
            : `${left.slice(0, columns)}\n${right.slice(-columns).padStart(columns, ' ')}`
        for (const part of line.split('\n')) this.line(part, 0, width, height)
    }

    rule(width: number, height: number) {
        this.line('-'.repeat(columnsFor(width)), 1, width, height)
    }

    item(qty: string, name: string, right: string, width: number, height: number) {
        const columns = columnsFor(width)
        const qtyFieldWidth = Math.min(4, Math.max(2, qty.length + 1))
        const qtyField = `${qty} `.slice(0, qtyFieldWidth).padEnd(qtyFieldWidth, ' ')
        const nameWidth = columns - qtyFieldWidth - (right ? right.length + 1 : 0)
        if (!right || nameWidth < 6) {
            wrapWords(name, Math.max(1, columns - qtyFieldWidth)).forEach((part, index) => {
                this.line(index === 0 ? `${qtyField}${part}` : `${' '.repeat(qtyFieldWidth)}${part}`, 0, width, height)
            })
            if (right) this.line(right.slice(-columns).padStart(columns, ' '), 0, width, height)
            return
        }
        wrapWords(name, nameWidth).forEach((part, index) => {
            if (index === 0) this.line(`${qtyField}${part.padEnd(nameWidth, ' ')} ${right}`, 0, width, height)
            else this.line(`${' '.repeat(qtyFieldWidth)}${part}`, 0, width, height)
        })
    }

    qr(data: string) {
        for (const part of wrapWords(data, COLUMNS)) this.centered(part, 1, 1)
    }

    finish(_beep?: { enabled?: boolean; count?: number }, feeds = 3): Uint8Array {
        for (let i = 0; i < feeds; i++) this.bytes.push(0x0a)
        this.bytes.push(ESC, 0x64, 0x02)
        return Uint8Array.from(this.bytes)
    }
}

export function starImpactLines(lines: string[]): Uint8Array {
    const out = new StarImpactText()
    for (const line of lines) out.centered(line, 1, 1)
    return out.finish()
}
